import { Building2, ExternalLink, LogOut, User } from "lucide-react";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { useOpenUrl } from "../../../hooks/useOpenUrl";
import { useAuth } from "../../Auth/UnifiedAuthProvider";
import { useNeutralino } from "../../Neutralino/NeutralinoContextProvider";
import { usePlatform } from "../../Platform/PlatformProvider";
import { AppSettingsCard } from "./AppSettingsCard";

export const AppSettingsUI = () => {
  const isDesktop = useIsDesktop();
  const { logout } = usePlatform();
  const { user, organization } = useAuth();
  const openUrl = useOpenUrl();
  const { isAlwaysOnTop, toggleAlwaysOnTop } = useNeutralino();

  const goToHelp = useCallback(() => {
    const url = "https://intercom.help/frontier-ai/en";
    openUrl(url);
  }, [openUrl]);

  const goToDashboard = useCallback(() => {
    const dashUrl = import.meta.env.VITE_DASH_URL;
    if (dashUrl) {
      openUrl(dashUrl);
    }
  }, [openUrl]);

  const handleAlwaysOnTopToggle = useCallback(() => {
    toggleAlwaysOnTop();
  }, [toggleAlwaysOnTop]);

  return (
    <div className="flex h-full flex-col space-y-4 overflow-y-auto px-3 pb-4">
      {/* User Info Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium">Account</h2>
          <Button
            onClick={logout}
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-gray-600 hover:text-gray-900"
          >
            <LogOut className="h-3 w-3" />
            <span className="ml-1">Log out</span>
          </Button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-gray-500" />
            <span className="text-gray-600">
              {user?.email || "Unknown user"}
            </span>
          </div>
          {organization && (
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-gray-600">{organization.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard Section */}
      <AppSettingsCard
        title="Dashboard"
        description="Administer your calendar, preferences and integrations."
      >
        <Button
          onClick={goToDashboard}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 h-8 px-3"
        >
          <ExternalLink className="h-3 w-3" />
          <span>Open Dashboard</span>
        </Button>
      </AppSettingsCard>

      {/* App Settings Section */}
      <div>
        <h2 className="mb-3 text-base font-medium">App settings</h2>
        <div className="flex flex-col space-y-4">
          <AppSettingsCard
            title="Always on top"
            description="Make the desktop application sit on top of all other windows."
          >
            <Switch
              checked={isDesktop && isAlwaysOnTop}
              onCheckedChange={handleAlwaysOnTopToggle}
              disabled={!isDesktop}
            />
          </AppSettingsCard>
        </div>
      </div>

      {/* Support Section */}
      <AppSettingsCard
        title="Support"
        description="Need help? Visit our help center for documentation and support."
      >
        <Button
          onClick={goToHelp}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 h-8 px-3"
        >
          <ExternalLink className="h-3 w-3" />
          <span>Get Help</span>
        </Button>
      </AppSettingsCard>
    </div>
  );
};
